import { useCallback } from "react";
import { useFetch } from "../common/useFetch";
import { useEmitMessage } from "../common/useEmitMessage";
import { SubscriptionState } from "../App/useSubscription";
import { PlanGroupProduct } from "./useDeliveryPlan";
import { CUSTOMER_API_URL } from '../../constants';
import axios from 'axios';

/**
 * 商品の追加を担う
 */
export const useSubscriptionLineAdd = (
  subscription: SubscriptionState,
  token: string,
  successCallback?: () => void,
) => {

  const emitMessage = useEmitMessage();
  const failed = () => emitMessage('app-error', 'keep');
  const success = () => {
    emitMessage('app-success-addline')
    if (successCallback) successCallback();
  }

  const [ addSubscriptionLineState, callAddSubscriptionLine ] =
    useFetch('addSubscriptionLineToFirstSubscription', token, success, failed);

  const checkVariantSoldOut = useCheckVariantSoldOut(subscription, token);

  const addSubscriptionLine = useCallback((
    planId: string,
    variantId: string,
    quantity: number,
    notValidCallback: () => void,
  ) => {
    emitMessage('app-loading-addline', 'keep');
    // 現在アプリ側で動作していない様子のため、コメントアウト
    // checkVariantSoldOut(
    //   variantId, 
    //   () => callAddSubscriptionLine({ planId, variantId, quantity }),
    //   notValidCallback
    // );
    callAddSubscriptionLine({ planId, variantId, quantity })
  }, [subscription, token]);

  return [ 
    addSubscriptionLineState.loading,
    addSubscriptionLine 
  ] as [ boolean, SubscriptionLineAdd ];
}

export type SubscriptionLineAdd = (planId: string, variantid: string, quantity: number, notValidCallback?: () => void) => void;

/**
 * 商品の削除を担う
 */
export const useSubscriptionLineRemove = (
  subscription: SubscriptionState,
  token: string,
  successCallback?: () => void,
) => {

  const emitMessage = useEmitMessage();
  const emitMessageWithModal = useEmitMessage('modal');

  const failed = () => emitMessage('app-error', 'keep');
  const success = () => {
    emitMessage('app-success-removeline')
    if (successCallback) successCallback();
  }

  const [ removeSubscriptionLineState, callRemoveSubscriptionLine ] =
    useFetch('removeSubscriptionLine', token, success, failed);

  const removeSubscriptionLine = useCallback((
    lineId: string
  ) => {
    if (!subscription) return;
    if (subscription.subscriptionLines.length === 1) {
      emitMessageWithModal('app-ban-empty');
      return;
    }

    emitMessage('app-loading-removeline', 'keep');
    const params = {
      subscriptionId: subscription.subscriptionContractId,
      subscriptionLine: { lineId },
    }
    callRemoveSubscriptionLine(params);
  }, [subscription, token]);

  return [ 
    removeSubscriptionLineState.loading,
    removeSubscriptionLine 
  ] as [ boolean, SubscriptionLineRemove ];
}

export type SubscriptionLineRemove = (lineId: string) => void;

/**
 * 商品の数量変更を担う
 */
export const useSubscriptionLineUpdate = (
  subscription: SubscriptionState,
  token: string,
  successCallback?: () => void,
) => {

  const emitMessage = useEmitMessage();
  const failed = () => emitMessage('app-error', 'keep');
  const success = () => {
    emitMessage('app-success-updateline')
    if (successCallback) successCallback();
  }

  const emitMessageWithModal = useEmitMessage('modal');

  const [ updateSubscriptionLineState, callUpdateSubscriptionLine ] =
    useFetch('updateSubscriptionLine', token, success, failed);

  const checkVariantSoldOut = useCheckVariantSoldOut(subscription, token);

  const updateSubscriptionLine = useCallback((
    variantId: string,
    lineId: string,
    quantity: number,
    notValidCallback?: () => void
  ) => {
    if (!subscription) return;
    if (subscription.subscriptionLines.length === 1 && quantity === 0) {
      emitMessageWithModal('app-ban-empty');
      return;
    }

    emitMessage('app-loading-updateline', 'keep');
    const params = {
      subscriptionId: subscription.subscriptionContractId,
      lineId,
      quantity
    }

    if (notValidCallback) {
      checkVariantSoldOut(
        variantId, 
        () => callUpdateSubscriptionLine(params),
        notValidCallback
      );
    } else {
      callUpdateSubscriptionLine(params);
    }
  }, [subscription, token]);

  return [ 
    updateSubscriptionLineState.loading,
    updateSubscriptionLine 
  ] as [ boolean, SubscriptionLineUpdate ];
}

export type SubscriptionLineUpdate = (variantId: string, lineId: string, quantity: number, notValidCallback?: () => void) => void;

// 在庫判定用
// 在庫によってアラートを出す設定が有効化されている場合に利用
const useCheckVariantSoldOut = (
  subscription: SubscriptionState,
  token: string
) => {
  const emitMessage = useEmitMessage();

  return (
    variantId: string,
    callback: Function,
    notValidCallback: Function
  ) => {
    if (subscription?.shop.showStockAlert) {
      (async () => {
        try {
          const response = await axios.post(`${CUSTOMER_API_URL}?action=checkVariantSoldOut`, { variantId }, { headers: { Authorization: token } });

          if (response.data.soldOut === true) {
            emitMessage('app-soldout-variant');
            if (notValidCallback) notValidCallback();
            return;
          } else {
            callback();
          }
        } catch (error) {
          emitMessage('app-error', 'keep');
          console.log(error);
          if (notValidCallback) notValidCallback();
        }
      })();
    } else {
      callback();
    }
  }
}